import {ForecastPageData, ForecastViewParams, WeatherForecastDay, WeatherForecastLocation} from '@/app/interfaces/weather';
import {daysModeMaximumDaysCount, ForecastDaysMode} from '@/app/constants/forecast';
import {FetcherServerError} from '@/common/services/fetcher/fetcherErrors';
import {createAsyncThunk} from '@/app/store/createAsyncThunk';
import {settlementExtraDataFetcher} from './helpers/settlementExtraDataFetcher';

/**
 * Fetch full data set to show the forecast page by a directly defined settlement
 */
export const fetchSettlementForecastDataAction = createAsyncThunk<
	{
		language: string;
		locationId: string;
		daysMode: ForecastDaysMode;
		selectedDate?: string;
		selectedWeekday?: number;
	},
	{
		view: ForecastViewParams;
		data: ForecastPageData;
	},
	{reason: 'locationNotFound' | 'noData' | 'invalidDate'}
>('forecast/fetchSettlementForecastData', async (params, {context, getState, success, failure}) => {
	const {language, locationId, daysMode, selectedDate, selectedWeekday} = params;
	const {backendAPI} = context;

	// Fetch a forecast data
	let forecast: WeatherForecastDay[] = [];
	let location: WeatherForecastLocation;
	try {
		({forecast, location} = await backendAPI.forecast.getForecast(
			language,
			locationId,
			daysModeMaximumDaysCount, // Use maximum days count to do not do an extra requests on day modes switching
			undefined,
			selectedDate,
		));
	} catch (error) {
		if (!(error instanceof FetcherServerError)) {
			throw error;
		}

		switch (error.code) {
			// Determinate error of the non existing location
			case 'LOCATION_NOT_FOUND':
			case 'LOCATION_IS_NOT_SETTLEMENT':
				return failure({reason: 'locationNotFound'});

			// Determinate error of the data is invalid or out of available range
			case 'DATE_OUT_OF_RANGE':
			case 'INVALID_DATE':
				return failure({reason: 'invalidDate'});

			// Determinate error as a non existing weather data (edge case)
			case 'FORECAST_NOT_AVAILABLE':
				return failure({reason: 'noData'});
		}

		throw error;
	}

	const [{regionTopLocationWeather, countryTopLocations}, metaData] = await Promise.all([
		// Fetch an extra data
		settlementExtraDataFetcher({language, location, context, getState}),

		// Fetch a metadata
		backendAPI.cachedRequest(backendAPI.misc.getPageMeta, [
			{
				locationAlternateIds: {locationId},
				locationsStableId: {
					ids: [locationId, location.superior.country?.id].filter(Boolean) as string[],
				},
			},
		]),
	]);

	return success({
		view: {
			daysMode,
			selectedDate,
			selectedWeekday,
		},
		data: {
			language,
			locationMeta: {
				alternateIds: metaData.locationAlternateIds,
				stableId: metaData.locationsStableId[0],
				countryStableId: metaData.locationsStableId[1],
			},
			location,
			forecast,
			extra: {
				type: 'settlementPage',
				regionTopLocationWeather,
				countryTopLocations,
			},
		},
	});
});
